import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { ManageCrossSkilling } from "../../../../export/cross-skilling";
import { FilterProvider } from "../../../../ts/components/filters/filter-context";
import JobsMatrix from "../../../../ts/components/jobs-matrix";
import { StoreType } from "../../../utils/types";
import { closeSkillInsightDetails } from "../../insight-dashboard/slices/skill-insight-report-slice";
import { closeWorkerInsightDetails } from "../../insight-dashboard/slices/worker-insight-report-slice";
import useCrossSkillingAccess from "../controllers/use-cross-skilling-access";
import WorkerInsightReport from "./cross-skilling-dashboard";

type TabKey = "skills-matrix" | "summary" | "jobs-matrix";

interface DashboardTab {
	key: TabKey;
	labelKey: string;
	content: React.ReactNode;
}

interface TabPanelProps {
	children: React.ReactNode;
	value: TabKey;
	tabKey: TabKey;
}

const TAB_SX = {
	fontFamily: "Roboto",
	fontSize: "14px",
	fontWeight: "500",
	lineHeight: "24px",
	letterSpacing: "0.4px",
};

function TabPanel({ children, value, tabKey }: TabPanelProps) {
	return (
		<div
			role='tabpanel'
			hidden={value !== tabKey}
			id={`simple-tabpanel-${tabKey}`}
			aria-labelledby={`simple-tab-${tabKey}`}
		>
			{value === tabKey && <Box>{children}</Box>}
		</div>
	);
}

function a11yProps(tabKey: TabKey) {
	return {
		id: `simple-tab-${tabKey}`,
		"aria-controls": `simple-tabpanel-${tabKey}`,
	};
}

const CrossSkillingDashboardTabs = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { canViewSkillsMatrix, canViewSummary, canViewJobsMatrix } =
		useCrossSkillingAccess();

	const currentUserId = useSelector(
		(state: StoreType) => state.user?.userData?.id
	);

	// Tabs are keyed rather than indexed: any of them can be hidden, by a
	// company flag or by a missing permission, so a position says nothing
	// about which tab it is.
	const tabs = useMemo(() => {
		const visibleTabs: DashboardTab[] = [];
		if (canViewSkillsMatrix) {
			visibleTabs.push({
				key: "skills-matrix",
				labelKey: "cross-skilling-dashboard_skills-matrix",
				content: (
					<FilterProvider filterKey={`skill-matrix-${currentUserId}`}>
						<ManageCrossSkilling />
					</FilterProvider>
				),
			});
		}
		if (canViewSummary) {
			visibleTabs.push({
				key: "summary",
				labelKey: "cross-skilling-dashboard_summary",
				content: (
					<FilterProvider filterKey={`cross-skilling-summary-${currentUserId}`}>
						<WorkerInsightReport />
					</FilterProvider>
				),
			});
		}
		if (canViewJobsMatrix) {
			visibleTabs.push({
				key: "jobs-matrix",
				labelKey: "cross-skilling-dashboard_jobs-matrix",
				content: (
					<FilterProvider filterKey={`jobs-matrix-${currentUserId}`}>
						<JobsMatrix />
					</FilterProvider>
				),
			});
		}
		return visibleTabs;
	}, [canViewSkillsMatrix, canViewSummary, canViewJobsMatrix, currentUserId]);

	const [selectedTab, setSelectedTab] = React.useState<TabKey | undefined>(
		tabs[0]?.key
	);
	// Fall back to the first visible tab if the selected one disappears, e.g.
	// a refreshed permissions array no longer grants it.
	const value = tabs.some((tab) => tab.key === selectedTab)
		? (selectedTab as TabKey)
		: tabs[0]?.key;

	useEffect(() => {
		dispatch(closeSkillInsightDetails());
		dispatch(closeWorkerInsightDetails());
	}, [dispatch]);

	const handleChange = useCallback(
		(_event: React.SyntheticEvent, newValue: TabKey) => {
			dispatch(closeSkillInsightDetails());
			dispatch(closeWorkerInsightDetails());

			setSelectedTab(newValue);
		},
		[dispatch]
	);

	// The page redirects away when nothing is visible; render nothing meanwhile.
	if (!value) return null;

	// A lone tab renders without a tab bar. This covers GAT-7289's Jobs Matrix
	// only page and a Skills Matrix only page.
	if (tabs.length === 1) return <>{tabs[0].content}</>;

	return (
		<Box sx={{ width: "100%", height: "100%", marginTop: -0.9 }}>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }} pl={1}>
				<Tabs
					value={value}
					onChange={handleChange}
					aria-label={t("cross-skilling-dashboard_workers-and-summary-tabs")}
					sx={{
						".MuiTab-root": {
							paddingBottom: 0.8,
						},
					}}
					TabIndicatorProps={{
						sx: {
							height: "1px",
							marginLeft: "15px",
						},
					}}
				>
					{tabs.map((tab) => (
						<Tab
							key={tab.key}
							value={tab.key}
							label={t(tab.labelKey)}
							{...a11yProps(tab.key)}
							sx={TAB_SX}
						/>
					))}
				</Tabs>
			</Box>

			{tabs.map((tab) => (
				<TabPanel key={tab.key} value={value} tabKey={tab.key}>
					{tab.content}
				</TabPanel>
			))}
		</Box>
	);
};

export default CrossSkillingDashboardTabs;
